import type MainProject from '@/model/MainProject'
import type DataFileMap from '@/model/Resource/DataFileMap'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import {
  projectOssSaveService,
  uploadVirtualResourceRootsToOss,
  type ProjectOssSaveProgress,
  type ProjectOssSaveResult,
} from '@/features/project-cloud/ossSave'
import BrowserUtil from '@/platform/oss/browserUtil'
import type { useAuthStore } from '@/features/auth'

type AuthStore = ReturnType<typeof useAuthStore>

/** 判断当前工程是否具备保存到 OSS 的最小条件。 */
export function canSaveProjectToOss(
  project: MainProject | null,
  auth: AuthStore,
  ownerUid?: number | null,
): boolean {
  if (!project) return false
  if (ownerUid && auth.uid !== ownerUid) return false
  return Boolean(auth.loggedIn && auth.uid && project.projectHeader.ID?.trim())
}

function isCloudSessionExpiredError(error: unknown): boolean {
  const message = String(
    (error && typeof error === 'object'
      ? (error as { message?: unknown }).message
      : error) ?? '',
  ).toLowerCase()

  return [
    'invalid session',
    'session invalid',
    'session expired',
    '登录态已失效',
    '会话已失效',
    '会话失效',
    '请重新登录',
  ].some((keyword) => message.includes(keyword))
}

/** 将当前工程保存到 OSS，登录态失效时刷新 session 后重试一次。 */
export async function saveWorkspaceProjectToOss(params: {
  project: MainProject
  virtualResourceRoots: Record<ResourceCategoryKey, DataFileMap | null>
  auth: AuthStore
  ownerUid?: number | null
  publishGindex: string | null
  onProgress?: (progress: ProjectOssSaveProgress) => void
}): Promise<ProjectOssSaveResult> {
  if (!params.auth.loggedIn || !params.auth.uid) {
    throw new Error('请先登录后再保存到 OSS')
  }
  if (params.ownerUid && params.auth.uid !== params.ownerUid) {
    throw new Error('当前登录账号与打开工程的账号不一致，请返回起始页后重新打开作品')
  }

  const runSave = async () => await projectOssSaveService.save({
    project: params.project,
    virtualResourceRoots: params.virtualResourceRoots,
    uid: params.auth.uid,
    publishGindex: params.publishGindex,
    onProgress: params.onProgress,
  })

  try {
    return await runSave()
  } catch (error) {
    if (!isCloudSessionExpiredError(error)) {
      throw error
    }
    if (!await params.auth.refreshSessionForRetry()) {
      throw new Error('登录态校验失败，请重新登录后再保存工程')
    }
    BrowserUtil.getInstance().connect(params.auth.uid)
    return await runSave()
  }
}

/** 将当前虚拟资源映射同步到 OSS `current/res.json`，登录态失效时刷新 session 后重试一次。 */
export async function uploadWorkspaceVirtualResourcesToOss(params: {
  project: MainProject
  virtualResourceRoots: Record<ResourceCategoryKey, DataFileMap | null>
  auth: AuthStore
  ownerUid?: number | null
}): Promise<string> {
  if (!params.auth.loggedIn || !params.auth.uid) {
    throw new Error('请先登录后再同步虚拟资源映射')
  }
  if (params.ownerUid && params.auth.uid !== params.ownerUid) {
    throw new Error('当前登录账号与打开工程的账号不一致，请返回起始页后重新打开作品')
  }

  const runUpload = async () => await uploadVirtualResourceRootsToOss({
    project: params.project,
    virtualResourceRoots: params.virtualResourceRoots,
    uid: params.auth.uid,
  })

  try {
    return await runUpload()
  } catch (error) {
    if (!isCloudSessionExpiredError(error)) {
      throw error
    }
    if (!await params.auth.refreshSessionForRetry()) {
      throw new Error('登录态校验失败，请重新登录后再同步虚拟资源映射')
    }
    BrowserUtil.getInstance().connect(params.auth.uid)
    return await runUpload()
  }
}
